import classNames from "classnames";
import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { get } from "lodash";
import { useForm, useFormState } from "react-final-form";
import IconButton from "components/IconButton";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import LineTypeIndicator from "../../../LineTypeIndicator";
import { prepareProcessDataViewerSourceDataForSelect, } from "../../../ResultElementBaseSourceSelect/utils";
import ResultElementBaseSourceSelect from "../../../ResultElementBaseSourceSelect";
import { parameterTypesSelector, processVariablesOptionsSelector, processVariablesSelector, } from "store/global/global.selectors";
import { persistedInsightsDataSelector, workspaceInsightsSelector, workspaceOptimizationsSelector, workspaceProcessDataSelector, workspaceRecipesSelector, workspaceSimulationsSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { calculationGraphElementSourceResultSelector, getActiveResultSetIdSelector, } from "store/rs-workspace/selectors/results.selectors";
import { ProcessDataViewerSourceVariableType, ResultElementGraphViewType, ResultElementSourceType, } from "store/rs-workspace/rs-workspace.types";
import { VolumeUnitsType } from "store/recipe/recipe.types";
import { SourceFields, SourceSourceFields } from "../../types";
import { parseComplexUnitToHtml } from "store/recipe/custom-variables.utils";
import { findDictionaryItemByValue, recipeParameterUnitsType } from "shared/rsDictionary";
import { getRecipeParameterUnitsItems, getSourceLineColor } from "store/rs-workspace/utils/results.utils";
import { getInsightSourceName, getRecipesCustomVariables, getSimulationSourceName, } from "store/rs-workspace/utils/scenario.utils";
import { isNotEmpty } from "utils/validate";
import DeleteIcon from "icons/delete.svg";
import * as styles from "../../styles.module.scss";
export var ProcessDataSourceRowTestIds;
(function (ProcessDataSourceRowTestIds) {
    ProcessDataSourceRowTestIds["rowElement"] = "rowElement";
    ProcessDataSourceRowTestIds["variableField"] = "variableField";
    ProcessDataSourceRowTestIds["sourceField"] = "sourceField";
    ProcessDataSourceRowTestIds["unitField"] = "unitField";
})(ProcessDataSourceRowTestIds || (ProcessDataSourceRowTestIds = {}));
export const viewTypeOptions = [
    { value: ResultElementGraphViewType.MedianWithSpread, label: "Median with spread" },
    { value: ResultElementGraphViewType.Median, label: "Median" },
    { value: ResultElementGraphViewType.MeanWithSpread, label: "Mean with spread" },
];
const SourceRow = ({ name, source, elementData, onRemoveSource, restrictions, index }) => {
    const form = useForm();
    const formState = useFormState();
    const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
    const resultSetId = useSelector(getActiveResultSetIdSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const simulations = useSelector(workspaceSimulationsSelector);
    const optimizations = useSelector(workspaceOptimizationsSelector);
    const insights = useSelector(workspaceInsightsSelector);
    const persistedInsightsData = useSelector(persistedInsightsDataSelector);
    const parameterTypes = useSelector(parameterTypesSelector);
    const processVariables = useSelector(processVariablesSelector);
    const processVariableOptionsList = useSelector(processVariablesOptionsSelector);
    const processData = useSelector(workspaceProcessDataSelector);
    const sourceResult = useSelector(calculationGraphElementSourceResultSelector({ resultSetId, resultSetElementId: elementData.id }, elementData.type, source.id));
    const { Insight: insightRestrictions = {}, Simulation: simulationRestrictions = {}, ...sourceRestrictions } = useMemo(() => restrictions?.Source || {}, [restrictions]);
    const processVariableOptions = useMemo(() => processVariableOptionsList.map((item) => ({
        ...item,
        variableType: ProcessDataViewerSourceVariableType.ProcessVariable,
    })), [processVariableOptionsList]);
    const error = useMemo(() => {
        if (!formState.pristine) {
            return "";
        }
        return sourceResult?.error || sourceResult?.sourceError;
    }, [formState.pristine, sourceResult?.sourceError, sourceResult?.error]);
    const customVariablesList = useMemo(() => {
        const recipeId = simulations.find(({ id }) => id === source?.source?.simulationId)?.recipeId;
        const customVariableId = source?.source?.[SourceSourceFields.customVariableId];
        return getRecipesCustomVariables(recipes, [recipeId], ({ recordInData, formula, id }) => isNotEmpty(customVariableId)
            ? Boolean(recordInData) && (!!formula?.complete || id === customVariableId)
            : Boolean(recordInData) && !!formula?.complete);
    }, [recipes, simulations, source]);
    const variablesOptions = useMemo(() => {
        const customVariableId = source?.source?.[SourceSourceFields.customVariableId];
        const customVariableRegularOptions = customVariablesList?.map(({ id, name, formula }) => {
            const unitHtml = parseComplexUnitToHtml(formula?.configuredUnits?.complexValue)?.html;
            return {
                value: id ?? 0,
                units: unitHtml,
                label: name ?? "",
                variableType: ProcessDataViewerSourceVariableType.Custom,
                disabled: isNotEmpty(customVariableId) && id === customVariableId && !formula?.complete,
            };
        });
        const customVariablesOptions = customVariableRegularOptions.length > 0
            ? [
                {
                    value: "Custom Variables",
                    label: "Custom Variables",
                    variableType: ProcessDataViewerSourceVariableType.Custom,
                    isGroupName: true,
                    disabled: true,
                },
                ...customVariableRegularOptions,
            ]
            : [];
        if (source?.source?.type === ResultElementSourceType.Simulation) {
            return [...processVariableOptions, ...customVariablesOptions];
        }
        if (source?.source?.type === ResultElementSourceType.ProcessData) {
            const { processDataId } = { ...source?.source };
            const selectedProcessData = processData?.find(({ id }) => id === processDataId);
            const liquidsOptions = (selectedProcessData?.state?.liquids ?? []).map(({ name }) => ({
                value: name,
                label: name,
                units: VolumeUnitsType.L,
                variableType: ProcessDataViewerSourceVariableType.Liquid,
            }));
            return [
                ...processVariableOptions,
                {
                    value: "Liquids",
                    label: "Liquids",
                    variableType: ProcessDataViewerSourceVariableType.Liquid,
                    isGroupName: true,
                    disabled: true,
                },
                ...liquidsOptions,
            ];
        }
        return [...processVariableOptions, ...customVariablesOptions];
    }, [customVariablesList, processVariableOptions, source?.source, processData]);
    const isProcessData = useMemo(() => get(formState.values, `${name}.${SourceFields.source}.${SourceSourceFields.type}`) ===
        ResultElementSourceType.ProcessData, [formState.values, name]);
    const isInsight = useMemo(() => get(formState.values, `${name}.${SourceFields.source}.${SourceSourceFields.type}`) ===
        ResultElementSourceType.Insight, [formState.values, name]);
    const sourceSelected = useMemo(() => !!get(formState.values, `${name}.${SourceFields.source}`), [formState.values, name]);
    const unitsOptions = useMemo(() => {
        const selectedUnit = source?.source?.[SourceSourceFields.units]?.value;
        const options = getRecipeParameterUnitsItems("", selectedUnit, parameterTypes, [], true);
        return options.filter(({ value }) => value !== selectedUnit);
    }, [parameterTypes, source]);
    const onSourceChange = useCallback((values) => {
        let sourceSourcePayload = {};
        let sourceName = "";
        const getSourceSourceBasePayload = (isSourceChanged) => ({
            ...(isSourceChanged
                ? {
                    ...source?.source,
                    [SourceSourceFields.variableType]: null,
                    [SourceSourceFields.liquid]: null,
                    [SourceSourceFields.customVariableId]: null,
                    [SourceSourceFields.target]: null,
                    [SourceSourceFields.units]: null,
                    [SourceSourceFields._variableName]: null,
                }
                : source?.source),
            [SourceSourceFields._sourceName]: sourceName,
            [SourceSourceFields.type]: values.type,
        });
        const conditions = [source[SourceFields.source]?.type !== values.type];
        switch (values.type) {
            case ResultElementSourceType.Simulation: {
                const { simulationId, optimizationId, adjustmentId } = values;
                conditions.push(source?.source?.[SourceSourceFields.simulationId] !== simulationId);
                sourceName = getSimulationSourceName(simulations, optimizations, simulationId, optimizationId, adjustmentId);
                sourceSourcePayload = {
                    ...getSourceSourceBasePayload(conditions.some(Boolean)),
                    [SourceSourceFields.processDataId]: null,
                    [SourceSourceFields.simulationId]: simulationId,
                    [SourceSourceFields.optimizationId]: optimizationId,
                    [SourceSourceFields.adjustmentId]: adjustmentId,
                };
                break;
            }
            case ResultElementSourceType.ProcessData: {
                const { processDataId } = values;
                conditions.push(source?.source?.[SourceSourceFields.processDataId] !== processDataId);
                sourceName = processData?.find(({ id }) => id === processDataId)?.name;
                sourceSourcePayload = {
                    ...getSourceSourceBasePayload(conditions.some(Boolean)),
                    [SourceSourceFields.simulationId]: null,
                    [SourceSourceFields.optimizationId]: null,
                    [SourceSourceFields.processDataId]: processDataId ?? 0,
                };
                break;
            }
            case ResultElementSourceType.Insight: {
                const { insightId, inputSetId, output } = values;
                conditions.push(source?.source?.[SourceSourceFields.insightId] !== insightId);
                sourceName = getInsightSourceName(insights, persistedInsightsData, processVariables, insightId, inputSetId, output, recipes);
                sourceSourcePayload = {
                    ...getSourceSourceBasePayload(conditions.some(Boolean)),
                    [SourceSourceFields.simulationId]: null,
                    [SourceSourceFields.optimizationId]: null,
                    [SourceSourceFields.processDataId]: null,
                    [SourceSourceFields.insightId]: insightId,
                    [SourceSourceFields.inputSetId]: inputSetId,
                    [SourceSourceFields.outputId]: output.id,
                };
                break;
            }
        }
        form.change(name, {
            ...source,
            source: sourceSourcePayload,
        });
        setSourceDialogOpen(false);
    }, [
        source,
        form,
        name,
        simulations,
        optimizations,
        processData,
        insights,
        persistedInsightsData,
        processVariables,
        recipes,
    ]);
    const onVariableChange = useCallback((option) => {
        const defaultUnit = findDictionaryItemByValue(recipeParameterUnitsType, option.units);
        let fieldsToReset = [];
        let targetField = "";
        form.batch(() => {
            form.change(`${name}.${SourceFields.source}.${SourceSourceFields._variableName}`, option);
            form.change(`${name}.${SourceFields.source}.${SourceSourceFields.units}`, defaultUnit);
            if (option?.variableType === ProcessDataViewerSourceVariableType.Custom) {
                fieldsToReset = [SourceSourceFields.variableType, SourceSourceFields.liquid];
                targetField = SourceSourceFields.customVariableId;
            }
            if (option?.variableType === ProcessDataViewerSourceVariableType.Liquid) {
                fieldsToReset = [SourceSourceFields.variableType, SourceSourceFields.customVariableId];
                targetField = SourceSourceFields.liquid;
            }
            if (option?.variableType === ProcessDataViewerSourceVariableType.ProcessVariable) {
                fieldsToReset = [SourceSourceFields.liquid, SourceSourceFields.customVariableId];
                targetField = SourceSourceFields.variableType;
            }
            fieldsToReset.forEach((field) => form.change(`${name}.${SourceFields.source}.${field}`, null));
            form.change(`${name}.${SourceFields.source}.${targetField}`, option?.value);
        });
    }, [form, name]);
    return (React.createElement("div", { "data-testid": ProcessDataSourceRowTestIds.rowElement, className: classNames(styles.row, styles.editMode, { [styles.errored]: !!error }) },
        React.createElement("div", { className: classNames(styles.cell, styles.source), "data-testid": ProcessDataSourceRowTestIds.sourceField },
            React.createElement(Input, { name: `${name}.${SourceFields.source}.${SourceSourceFields._sourceName}`, component: SelectInput, autoFocus: true, items: [{}], placeholder: source?.[SourceFields.source]?.[SourceSourceFields._sourceName] ?? "Select", itemRenderer: SelectItemRenderer, restrictions: sourceRestrictions, classes: {
                    container: styles.selectWrapper,
                    component: styles.select,
                    error: styles.error,
                }, disableFormChange: true, popoverProps: { onInteraction: setSourceDialogOpen }, externalError: error ?? "" }),
            sourceDialogOpen && (React.createElement(ResultElementBaseSourceSelect, { title: "Data source", allowedTypes: Object.values(ResultElementSourceType), initialValues: prepareProcessDataViewerSourceDataForSelect(source), onSubmit: onSourceChange, onClose: () => setSourceDialogOpen(false), withSimulationOptimization: true, restrictions: {
                    Insight: insightRestrictions,
                    Simulation: simulationRestrictions,
                    Type: sourceRestrictions,
                }, classes: { target: styles.sourceDialogTarget } }))),
        React.createElement("div", { className: classNames(styles.cell, styles.variable), "data-testid": ProcessDataSourceRowTestIds.variableField }, !isInsight && (React.createElement(Input, { name: `${name}.${SourceFields.source}.${SourceSourceFields._variableName}`, component: SelectInput, items: variablesOptions, placeholder: "Select", itemRenderer: SelectItemRenderer, classes: {
                container: styles.selectWrapper,
                component: styles.select,
            }, restrictions: simulationRestrictions?.VariableType, disableFormChange: true, onChange: onVariableChange, disabled: !sourceSelected }))),
        React.createElement("div", { className: classNames(styles.cell, styles.unit), "data-testid": ProcessDataSourceRowTestIds.unitField }, !isInsight && (React.createElement(Input, { name: `${name}.${SourceFields.source}.${SourceSourceFields.units}`, component: SelectInput, items: unitsOptions, itemRenderer: SelectItemRenderer, classes: {
                container: styles.selectWrapper,
                component: styles.select,
            }, restrictions: simulationRestrictions?.Units, placeholder: "Select" }))),
        React.createElement("div", { className: classNames(styles.cell, styles.line) },
            React.createElement(LineTypeIndicator, { color: getSourceLineColor(get(formState.values, `${name}.${SourceFields.color}`), index), onChange: (color) => form.change(`${name}.${SourceFields.color}`, color) })),
        React.createElement("div", { className: classNames(styles.cell, styles.viewType) }, !isProcessData && (React.createElement(Input, { name: `${name}.${SourceFields.viewType}`, component: SelectInput, items: viewTypeOptions, itemRenderer: SelectItemRenderer, valueFormatter: ({ value }) => value, classes: {
                container: styles.selectWrapper,
                component: styles.select,
            }, restrictions: {}, placeholder: "Select" }))),
        React.createElement("div", { className: classNames(styles.cell, styles.delete) },
            React.createElement(IconButton, { icon: React.createElement(DeleteIcon, null), title: "Delete", onClick: onRemoveSource }))));
};
export default SourceRow;
